import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/ClientLogin.css';
import morrowLogoWink from './morrow-logo-wink-animated.webp';

function ClientLogin({ setUser }) {
  const { language } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loginMethod, setLoginMethod] = useState('password');
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    const { error } = await supabase.auth.signInWithPassword({ email, password });

    if (error) {
      setError(language === 'en' ? 'Invalid email or password.' : 'Ungültige E-Mail oder Passwort.');
      setUser(null);
    } else {
      setMessage(language === 'en' ? 'Login successful!' : 'Anmeldung erfolgreich!');
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError || !userData.user) {
        setError(language === 'en' ? 'Failed to retrieve user session.' : 'Benutzersitzung konnte nicht abgerufen werden.');
        setUser(null);
      } else {
        setUser(userData.user);
        navigate('/dashboard');
      }
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`
      }
    });

    if (error) {
      setError(language === 'en' ? 'Failed to send magic link.' : 'Magic Link konnte nicht gesendet werden.');
    } else {
      setMessage(language === 'en' ? 'Magic link sent! Please check your email.' : 'Magic Link gesendet! Bitte überprüfen Sie Ihre E-Mails.');
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    setMessage('');

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin + '/dashboard',
      },
    });

    if (error) {
      setError(language === 'en' ? 'Google login failed.' : 'Google-Anmeldung fehlgeschlagen.');
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError(language === 'en' ? 'Please enter your email address first' : 'Bitte geben Sie zuerst Ihre E-Mail-Adresse ein');
      return;
    }

    setError('');
    setMessage('');

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset`
    });

    if (error) {
      setError(language === 'en' ? 'Failed to send password reset email.' : 'E-Mail zum Zurücksetzen des Passworts konnte nicht gesendet werden.');
    } else {
      setMessage(language === 'en' ? 'Password reset email sent! Please check your inbox.' : 'E-Mail zum Zurücksetzen des Passworts gesendet! Bitte überprüfen Sie Ihren Posteingang.');
    }
  };

  return (
    <div className="client-login-container">
      <div className="video-section">
        <img 
          src={morrowLogoWink} 
          alt="Morrow Logo" 
          className="morrow-logo"
        />
      </div>
      <div className="login-section">
        <div className="login-container">
          <h1 className="login-title">{language === 'en' ? 'Client Login' : 'Kunden-Login'}</h1>
          
          {error && <p className="error-message">{error}</p>}
          {message && <p className="success-message">{message}</p>}

          <div className="login-form">
            <form onSubmit={handleLogin}>
              <input
                type="email"
                placeholder={language === 'en' ? 'Email' : 'E-Mail'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <input
                type="password"
                placeholder={language === 'en' ? 'Password' : 'Passwort'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              {error && (
                <div className="reset-password">
                  <span className="forgot-password-link" onClick={handleResetPassword}>
                    {language === 'en' ? 'Forgot password?' : 'Passwort vergessen?'}
                  </span>
                </div>
              )}
              {email && (
                <button type="button" onClick={handleMagicLink} className="magic-link-button">
                  {language === 'en' ? 'Login with Email Link' : 'Mit E-Mail-Link anmelden'}
                </button>
              )}
              {password && (
                <button type="submit">{language === 'en' ? 'Login with Password' : 'Mit Passwort anmelden'}</button>
              )}
            </form>
            <div className="clientdivider">
              <span>{language === 'en' ? 'or' : 'oder'}</span>
            </div>
            <button onClick={handleGoogleLogin} className="google-login">
              <img
                src="https://developers.google.com/identity/images/g-logo.png"
                alt="Google Logo"
                className="google-icon"
              />
              {language === 'en' ? 'Login with Google' : 'Mit Google anmelden'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ClientLogin;
